import axios from 'axios';
import { t } from 'i18next';
import { endpoints } from '../../utils/constants';

export const getUserById = (division, userId) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/${division}/${userId}`);

export const createUser = (data) =>
  axios.post(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}`, data);

export const updateUserById = (data, userId) =>
  axios.put(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/${userId}`, data);

export const updateUserProfileById = (data, userId) =>
  axios.put(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/${userId}/profile`, data);

export const deleteUserById = (userId) =>
  axios.delete(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/${userId}`);

export const requestPasswordReset = (userEmail) => {
  const requestBody = { emailAddress: userEmail };
  return axios.post(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/password-reset`, requestBody);
};

export const validatePasswordResetToken = ({ token, userEmail }) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/password-reset`, {
    params: {
      token,
      email: userEmail,
    },
  });

export const passwordReset = ({ token, userEmail, newPassword }) => {
  const requestBody = {
    emailAddress: userEmail,
    newPassword,
    token,
  };
  return axios.put(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/password`, requestBody);
};

export const activate = ({ token, userEmail }) => {
  const requestBody = {
    emailAddress: userEmail,
    token,
  };
  return axios.put(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/activate`, requestBody);
};

export const getPreferences = (userId, division) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.userPreferences}/${userId}`, {
    params: {
      division,
    },
  });

export const updatePreferences = (userPreferences) =>
  axios.put(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.userPreferences}/${userPreferences.userId}`, userPreferences);

export const createPreferences = (userPreferences) =>
  axios.post(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.userPreferences}`, userPreferences);

export const getManageUserById = (division, userid, userId) =>
  axios.get(`${t('ApiEndPoint')}${t('ShippingApiVirtualFolder')}${endpoints.shipping.users}/${division}/${userId}/${userid}`);

/**
 * Remove a user from a specific company.
 * If the user has other companies, updates the user with the remaining companies.
 * If no companies remain, deletes the user entirely.
 * @param {string} division - The division (e.g., 'Truckmate')
 * @param {string} userEmail - The user email to identify the user
 * @param {number} companyIdToRemove - The company ID to remove the user from
 * @returns {Promise} - Resolves with the API response
 */
export const removeUserFromCompany = async (division, userEmail, companyIdToRemove) => {
  // First, get the user's current data using the email
  const userResponse = await getUserById(division, userEmail);
  const userData = userResponse.data;

  // Filter out the company to remove
  const remainingCompanies = userData.companies.filter(
    (company) => company.companyId !== companyIdToRemove,
  );

  // If no companies remain, delete the user entirely
  if (remainingCompanies.length === 0) {
    return deleteUserById(userData.userId);
  }

  // Otherwise, update the user with remaining companies
  const updatedUserData = {
    ...userData,
    division,
    companies: remainingCompanies,
  };

  return updateUserById(updatedUserData, userData.userId);
};

/**
 * Update user's active status for a specific company.
 * @param {string} division - The division (e.g., 'Truckmate')
 * @param {string} userEmail - The user email to identify the user
 * @param {number} companyId - The company ID to update status for
 * @param {boolean} isActive - Whether the user should be active for this company
 * @returns {Promise} - Resolves with the API response
 */
export const updateUserCompanyStatus = async (division, userEmail, companyId, isActive) => {
  // First, get the user's current data
  const userResponse = await getUserById(division, userEmail);
  const userData = userResponse.data;

  // Update the isActive status for the specific company
  const updatedCompanies = userData.companies.map((company) => {
    if (company.companyId === companyId) {
      return { ...company, isActive };
    }

    return company;
  });

  const updatedUserData = {
    ...userData,
    division,
    companies: updatedCompanies,
  };

  return updateUserById(updatedUserData, userData.userId);
};
